// src/services/api/InsuranceService.js
import ApiService from '../base/BaseApiService';
import PDFService from './PDFService';
import { getInsuranceTypeName } from '../../utils/insuranceTypeUtils';

// すべての保険会社を取得
export const fetchInsuranceCompanies = async (queryParams = {}) => {
  try {
    const searchParams = new URLSearchParams();

    Object.entries(queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v !== undefined && v !== null && v !== '') {
            searchParams.append(key, v);
          }
        });
      } else if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value);
      }
    });

    const queryString = searchParams.toString();
    const url = queryString ? `/insurance-companies/?${queryString}` : '/insurance-companies/';

    return await ApiService.get(url);
  } catch (error) {
    console.error('Error fetching insurance companies:', error);
    throw error;
  }
};

// 代理店と保険会社の紐づけデータを取得
export const fetchAgencyInsuranceCompanies = async (id) => {
  try {
    return await ApiService.get(`/agencies/${id}/insurance-companies/`);
  } catch (error) {
    console.error('Error fetching agency insurance companies:', error);
    throw error;
  }
};

/**
 * 複数の保険会社に対して削除履歴を追加
 * @param {number[]} companyIds - 削除対象の保険会社IDの配列
 * @param {number} agencyId - 代理店ID
 * @returns {Promise<Object>} - 削除結果
 */
export const deleteInsuranceCompanies = async (companyIds, agencyId) => {
  try {
    // クエリパラメータは使わず、bodyで渡す
    const url = `/insurance-applications/delete`;
    const data = {
      company_ids: companyIds,
      agency_id: agencyId
    };

    const response = await ApiService.delete(url, {
      data,
      showErrorDialog: true,
      timeout: 30000 // 30秒のタイムアウト
    });

    return response;

  } catch (error) {
    console.error('保険会社削除処理エラー:', error);
    throw error;
  }
};

/**
 * 保険会社をタイプ別にグループ化
 * @param {Array<Object>} companies - 保険会社オブジェクトの配列
 * @returns {Object<string, Array<Object>>} - insurance_typeごとにグループ化された保険会社
 */
export const groupCompaniesByType = (companies) => {
  const groups = {
    1: [], // 生命保険
    2: []  // 損害保険
  };

  if (companies && companies.length) {
    companies
      .filter(company => company.insurance_type in groups)
      .forEach(company => {
        groups[company.insurance_type].push(company);
      });
  }

  return groups;
}

// 保険会社をかな別にグループ化
export const groupCompaniesByKana = (companies) => {
  const groups = {
    '生命保険会社': {
      'あ行': [],
      'か行': [],
      'さ行': [],
      'た行': [],
      'な行': [],
      'は行': [],
      'ま行': [],
      'や行': [],
      'ら行': [],
      'わ行': []
    },
    '損害保険会社': {
      'あ行': [],
      'か行': [],
      'さ行': [],
      'た行': [],
      'な行': [],
      'は行': [],
      'ま行': [],
      'や行': [],
      'ら行': [],
      'わ行': []
    }
  };

  // 最初の文字でグループ化
  companies.forEach(company => {
    const firstChar = company.name_kana ? company.name_kana.charAt(0) : '';
    const insuranceTypeName = getInsuranceTypeName(company.insurance_type);
    if (!insuranceTypeName) {
      // 不明な保険タイプはスキップ
      return;
    }
    const companyType = `${insuranceTypeName}会社`;

    // 会社名・ID・保険会社種別を含むオブジェクト
    const companyData = {
      name: company.name,
      id: company.id,
      type: company.insurance_type
    };

    if (/^[あいうえおア-オ]/.test(firstChar)) {
      groups[companyType]['あ行'].push(companyData);
    } else if (/^[かきくけこカ-コ]/.test(firstChar)) {
      groups[companyType]['か行'].push(companyData);
    } else if (/^[さしすせそサ-ソ]/.test(firstChar)) {
      groups[companyType]['さ行'].push(companyData);
    } else if (/^[たちつてとタ-ト]/.test(firstChar)) {
      groups[companyType]['た行'].push(companyData);
    } else if (/^[なにぬねのナ-ノ]/.test(firstChar)) {
      groups[companyType]['な行'].push(companyData);
    } else if (/^[はひふへほハ-ホ]/.test(firstChar)) {
      groups[companyType]['は行'].push(companyData);
    } else if (/^[まみむめもマ-モ]/.test(firstChar)) {
      groups[companyType]['ま行'].push(companyData);
    } else if (/^[やゆよヤ-ヨ]/.test(firstChar)) {
      groups[companyType]['や行'].push(companyData);
    } else if (/^[らりるれろラ-ロ]/.test(firstChar)) {
      groups[companyType]['ら行'].push(companyData);
    } else {
      groups[companyType]['わ行'].push(companyData);
    }
  });

  // 空のグループを削除
  Object.keys(groups).forEach(inner => {
    Object.keys(groups[inner]).forEach(key => {
      if (groups[inner][key].length === 0) {
        delete groups[inner][key];
      }
    });
    if (Object.keys(groups[inner]).length === 0) {
      delete groups[inner];
    }
  });

  return groups;
};

// 保険会社が取り扱っている文書タイプフラグの取得
export const fetchInsuranceFlags = async (companyIds = []) => {
  try {
    if (!Array.isArray(companyIds) || companyIds.length === 0) return [];
    return await ApiService.post('/insurance-companies/flags', {
      company_ids: companyIds,
      flag_type: ['new_contract', 'maintenance', 'handbook', 'hikiuke'],
    });
  } catch (error) {
    console.error('Error fetching Insurance flags:', error);
    throw error;
  }
};

// 保険申請を作成する（拡張版：単一・バッチ対応）
export const createInsuranceApplication = async (
  agencyId,
  insuranceCompanyId,
  newContractFiles,
  maintenanceFiles,
  handbookFiles,
  hikiukeFiles
) => {
  try {
    console.log('単一申請開始:', {
      agencyId,
      insuranceCompanyId,
      newContractFiles: newContractFiles ? newContractFiles.length : 0,
      maintenanceFiles: maintenanceFiles ? maintenanceFiles.length : 0,
      handbookFiles: handbookFiles ? handbookFiles.length : 0,
      hikiukeFiles: hikiukeFiles ? hikiukeFiles.length : 0
    });

    // FormDataを作成
    const formData = new FormData();
    formData.append('agency_id', agencyId);
    formData.append('insurance_company_id', insuranceCompanyId);

    // バッチモードフラグは設定しない（デフォルトで単一申請）

    // 新契約ファイルを追加
    if (newContractFiles && newContractFiles.length > 0) {
      newContractFiles.forEach(file => {
        formData.append('new_contract_files', file);
      });
    }

    // 保全・収納・支払ファイルを追加
    if (maintenanceFiles && maintenanceFiles.length > 0) {
      maintenanceFiles.forEach(file => {
        formData.append('maintenance_files', file);
      });
    }

    // ハンドブックファイルを追加
    if (handbookFiles && handbookFiles.length > 0) {
      handbookFiles.forEach(file => {
        formData.append('handbook_files', file);
      });
    }

    // 引受目安ファイルを追加
    if (hikiukeFiles && hikiukeFiles.length > 0) {
      hikiukeFiles.forEach(file => {
        formData.append('hikiuke_files', file);
      });
    }

    // multipart/form-dataとしてPOSTリクエストを送信
    const result = await ApiService.post('/insurance-applications/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
      // タイムアウトは自動調整される
    });

    console.log('単一申請成功:', result);
    return result;

  } catch (error) {
    console.error('Error creating insurance application:', error);
    throw error;
  }
};

/**
 * バッチ申請処理（複数の保険会社に一括申請）
 * @param {number} agencyId - 代理店ID
 * @param {Array} applications - 申請データ配列
 * @param {Object} options - オプション
 * @returns {Promise} - API応答
 */
export const createBatchInsuranceApplications = async (agencyId, applications, options = {}) => {
  try {
    console.log('バッチ申請開始:', {
      agencyId,
      applicationCount: applications.length,
      applications: applications.map(app => ({
        companyId: app.companyId,
        companyName: app.companyName,
        newContractFiles: app.newContractFiles.length,
        hikiukeFiles: app.hikiukeFiles.length,
        maintenanceFiles: app.maintenanceFiles.length,
        handbookFiles: app.handbookFiles.length
      }))
    });

    const formData = new FormData();

    // 基本パラメータ
    formData.append('agency_id', agencyId.toString());

    // 申請メタデータを準備
    const applicationData = applications.map((app, index) => ({
      companyId: app.companyId,
      newContractFilesCount: app.newContractFiles.length,
      hikiukeFilesCount: app.hikiukeFiles.length,
      maintenanceFilesCount: app.maintenanceFiles.length,
      handbookFilesCount: app.handbookFiles.length
    }));

    formData.append('applications', JSON.stringify(applicationData));

    // ファイルを添付
    let totalFileCount = 0;
    applications.forEach((app, appIndex) => {
      // 新契約ファイル
      app.newContractFiles.forEach((file, fileIndex) => {
        const key = `app_${appIndex}_newContract_${fileIndex}`;
        formData.append(key, file);
        totalFileCount++;
        console.log(`ファイル追加: ${key} -> ${file.name} (${Math.round(file.size / 1024)}KB)`);
      });

      // 引受目安ファイル
      (app.hikiukeFiles || []).forEach((file, fileIndex) => {
        const key = `app_${appIndex}_hikiuke_${fileIndex}`;
        formData.append(key, file);
        totalFileCount++;
        console.log(`ファイル追加: ${key} -> ${file.name} (${Math.round(file.size / 1024)}KB)`);
      });

      // 保全ファイル
      app.maintenanceFiles.forEach((file, fileIndex) => {
        const key = `app_${appIndex}_maintenance_${fileIndex}`;
        formData.append(key, file);
        totalFileCount++;
        console.log(`ファイル追加: ${key} -> ${file.name} (${Math.round(file.size / 1024)}KB)`);
      });

      // ハンドブックファイル
      app.handbookFiles.forEach((file, fileIndex) => {
        const key = `app_${appIndex}_handbook_${fileIndex}`;
        formData.append(key, file);
        totalFileCount++;
        console.log(`ファイル追加: ${key} -> ${file.name} (${Math.round(file.size / 1024)}KB)`);
      });
    });

    console.log(`総ファイル数: ${totalFileCount}件`);

    // タイムアウト設定
    const timeoutMs = options.timeout || calculateTimeout(applications);

    // API呼び出し（拡張版BaseApiServiceを使用）
    const startTime = Date.now();

    const result = await ApiService.postBatch('/insurance-applications/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: timeoutMs
    }, {
      enableProgressLog: true,
      estimatedFileCount: totalFileCount,
      onProgress: options.onProgress // プログレス監視コールバック
    });
    const endTime = Date.now();
    const requestTime = endTime - startTime;

    console.log(`バッチAPI応答時間: ${requestTime}ms`);
    console.log('バッチ申請成功:', {
      batchMode: result.batch_mode,
      successCount: result.summary?.successCount,
      failureCount: result.summary?.failureCount,
      processingTime: result.summary?.processingTime,
      totalFiles: result.summary?.totalFiles
    });

    return result;

  } catch (error) {
    console.error('バッチ申請エラー:', error);

    // エラーの詳細な分類
    if (error.code === 'ECONNABORTED') {
      throw new Error('リクエストがタイムアウトしました。ファイルサイズが大きすぎる可能性があります。');
    } else if (error.message && error.message.includes('Network Error')) {
      throw new Error('ネットワークエラーが発生しました。接続を確認してください。');
    } else {
      throw error;
    }
  }
};
/**
 * 統一された申請処理関数（単一・バッチ自動選択）
 * @param {number} agencyId - 代理店ID
 * @param {Array} selectedCompanies - 選択された保険会社
 * @param {Object} uploadFiles - アップロードファイル
 * @param {Object} options - オプション
 * @returns {Promise} - 処理結果
 */
export const processInsuranceApplications = async (
  agencyId,
  selectedCompanies,
  uploadFiles,
  options = {}
) => {
  // 申請データを準備
  const applications = selectedCompanies
    .map(company => {
      const companyName = typeof company === 'object' && company !== null ? company.name : company;
      const companyId = typeof company === 'object' && company !== null ? company.id : null;

      if (!companyId) return null;

      const newContractFiles = uploadFiles[companyName]?.newContract?.map(item => item.file) || [];
      const maintenanceFiles = uploadFiles[companyName]?.maintenance?.map(item => item.file) || [];
      const handbookFiles = uploadFiles[companyName]?.handbook?.map(item => item.file) || [];
      const hikiukeFiles = uploadFiles[companyName]?.hikiuke?.map(item => item.file) || [];
      const hasFiles =
        newContractFiles.length > 0 ||
        maintenanceFiles.length > 0 ||
        handbookFiles.length > 0 ||
        hikiukeFiles.length > 0;

      if (!hasFiles) return null;

      return {
        companyId,
        companyName,
        newContractFiles,
        maintenanceFiles,
        handbookFiles,
        hikiukeFiles
      };
    })
    .filter(app => app !== null);

  if (applications.length === 0) {
    throw new Error('申請するファイルがありません');
  }

  console.log(`申請処理: ${applications.length}件`);

  // バッチ処理
  return await createBatchInsuranceApplications(agencyId, applications, options);
};

/**
 * ファイルサイズに基づいてタイムアウト時間を計算
 * @param {Array} applications - 申請データ配列
 * @returns {number} - タイムアウト時間（ミリ秒）
 */
const calculateTimeout = (applications) => {
  const totalSize = calculateTotalFileSize(applications);
  const baseTimeout = 30000; // 30秒

  if (totalSize > 100 * 1024 * 1024) { // 100MB以上
    return 180000; // 3分
  } else if (totalSize > 50 * 1024 * 1024) { // 50MB以上
    return 120000; // 2分
  } else if (totalSize > 20 * 1024 * 1024) { // 20MB以上
    return 90000; // 1.5分
  } else {
    return baseTimeout;
  }
};

/**
 * ファイルサイズの合計を計算
 * @param {Array} applications - 申請データ配列
 * @returns {number} - 総ファイルサイズ（バイト）
 */
export const calculateTotalFileSize = (applications) => {
  let totalSize = 0;

  applications.forEach(app => {
    app.newContractFiles.forEach(file => {
      totalSize += file.size || 0;
    });
    app.maintenanceFiles.forEach(file => {
      totalSize += file.size || 0;
    });
    (app.handbookFiles || []).forEach(file => {
      totalSize += file.size || 0;
    });
    (app.hikiukeFiles || []).forEach(file => {
      totalSize += file.size || 0;
    });
  });

  return totalSize;
};

/**
 * ファイルサイズを人間が読みやすい形式に変換
 * @param {number} bytes - ファイルサイズ（バイト）
 * @returns {string} - フォーマット済みサイズ
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * バッチ申請の事前検証
 * @param {Array} applications - 申請データ配列
 * @returns {Object} - 検証結果
 */
export const validateBatchApplications = (applications) => {
  const validation = {
    isValid: true,
    errors: [],
    warnings: [],
    summary: {
      totalApplications: applications.length,
      totalFiles: 0,
      totalSize: 0
    }
  };

  if (!applications || applications.length === 0) {
    validation.isValid = false;
    validation.errors.push('申請データが空です');
    return validation;
  }

  applications.forEach((app, index) => {
    const appLabel = `申請${index + 1}`;

    // 必須フィールドチェック
    if (!app.companyId) {
      validation.isValid = false;
      validation.errors.push(`${appLabel}: 保険会社IDが指定されていません`);
    }

    if (!app.companyName) {
      validation.warnings.push(`${appLabel}: 保険会社名が設定されていません`);
    }

    // ファイル数チェック
    const newContractCount = app.newContractFiles ? app.newContractFiles.length : 0;
    const maintenanceCount = app.maintenanceFiles ? app.maintenanceFiles.length : 0;
    const handbookCount = app.handbookFiles ? app.handbookFiles.length : 0;
    const hikiukeCount = app.hikiukeFiles ? app.hikiukeFiles.length : 0;
    const totalFiles = newContractCount + maintenanceCount + handbookCount + hikiukeCount;

    if (totalFiles === 0) {
      validation.isValid = false;
      validation.errors.push(`${appLabel} (${app.companyName}): アップロードファイルがありません`);
    }

    validation.summary.totalFiles += totalFiles;

    // ファイルサイズチェック
    let appTotalSize = 0;
    [...(app.newContractFiles || []), ...(app.maintenanceFiles || []), ...(app.handbookFiles || []), ...(app.hikiukeFiles || [])].forEach(file => {
      const fileSize = file.size || 0;
      appTotalSize += fileSize;

      // 単一ファイルサイズチェック（50MB制限）
      if (fileSize > 50 * 1024 * 1024) {
        validation.warnings.push(
          `${appLabel}: ファイル "${file.name}" が大きすぎます (${formatFileSize(fileSize)})`
        );
      }
    });

    validation.summary.totalSize += appTotalSize;
  });

  // 総ファイルサイズチェック（200MB制限）
  if (validation.summary.totalSize > 200 * 1024 * 1024) {
    validation.warnings.push(
      `総ファイルサイズが大きすぎます (${formatFileSize(validation.summary.totalSize)}). ` +
      'アップロードに時間がかかる可能性があります。'
    );
  }

  return validation;
};

// 保険申請の一覧を取得
export const fetchInsuranceApplications = async (skip = null, limit = null) => {
  try {
    let url = '/insurance-applications/';
    if (skip !== null && limit !== null) {
      url += `?skip=${skip}&limit=${limit}`;
    } else if (skip !== null) {
      url += `?skip=${skip}`;
    } else if (limit !== null) {
      url += `?limit=${limit}`;
    }
    return await ApiService.get(url);
  } catch (error) {
    console.error('Error fetching insurance applications:', error);
    throw error;
  }
};

// 保険申請を審査する (承認/却下)
export const reviewInsuranceApplication = async (applicationId, status, comment = '') => {
  try {
    const data = {
      status: status, // "approved" or "rejected"
      comment: comment
    };

    return await ApiService.post(`/insurance-applications/${applicationId}/review`, data);
  } catch (error) {
    console.error('Error reviewing insurance application:', error);
    throw error;
  }
};

// ドキュメントを表示するためのURL取得 (PDFService経由)
export const getDocumentViewUrl = (documentId) => {
  return PDFService.getDocumentViewUrl(documentId);
};

// 保険ドキュメントを表示するためのURL取得
export const getInsuranceDocumentViewUrl = (fileTitle) => {
  return PDFService.getInsuranceDocumentViewUrl(fileTitle);
};

// 保険ドキュメントをダウンロードするためのURL取得
export const getInsuranceDocumentDownloadUrl = (fileTitle) => {
  return PDFService.getInsuranceDocumentDownloadUrl(fileTitle);
};

// PDFファイルを直接取得する (PDFService経由)
export const fetchPdfDocument = async (url, params) => {
  try {
    // PDFServiceを使用してPDFを取得
    return await PDFService.fetchPdfDocument(url, params);
  } catch (error) {
    console.error('Error fetching PDF document:', error);
    throw error;
  }
};